import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PRODUCTION_URL = 'https://cthomeremodels.com'; // Hardcoded production URL - never use preview URL

const WelcomePage = ({ userRole }) => {
  const [emailCopied, setEmailCopied] = useState(false);
  
  // Users list for dropdown
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Send Welcome Email state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    recipient_name: '',
    login_email: '',
    login_password: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ type: '', message: '' });
  
  // Fetch users on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.get(`${API_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUsers(res.data.users || []))
        .catch(() => {});
    }
  }, []);
  
  // Handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      const user = users.find(u => u.email === userId);
      if (user) {
        setEmailForm({
          recipient_email: user.email,
          recipient_name: user.name || user.email.split('@')[0],
          login_email: user.email,
          login_password: '' // Password must be entered manually for security
        });
      }
    } else {
      setEmailForm({ recipient_email: '', recipient_name: '', login_email: '', login_password: '' });
    }
  };
  
  // Generate preview HTML
  const generatePreviewHTML = () => {
    const name = emailForm.recipient_name || 'Client';
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #5c0701, #8b1a15); color: white; padding: 25px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="margin: 0; font-size: 22px;">Welcome to Your CTHomeRemodels Dashboard!</h1>
          <p style="margin: 8px 0 0 0; font-size: 13px; opacity: 0.9;">Visser General Construction</p>
        </div>
        
        <div style="background: #f8f9fa; padding: 25px;">
          <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 15px;">
            <p style="margin: 0 0 15px 0;">Hi <strong>${name}</strong>,</p>
            <p style="margin: 0 0 15px 0;">Congratulations! Your professional website and marketing dashboard is ready.</p>
            
            <div style="background: #e8f4fd; padding: 15px; border-radius: 6px; margin: 15px 0;">
              <p style="margin: 0; font-size: 14px;"><strong>🎁 Your Website Package Value: $5,000+</strong></p>
              <p style="margin: 8px 0 0 0; font-size: 12px; color: #666;">This includes your custom website, CMS dashboard, lead management system, and all the features below.</p>
            </div>
          </div>
          
          <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 15px; border-left: 4px solid #3a87fe;">
            <h3 style="margin: 0 0 15px 0; color: #5c0701;">🔑 Your Login Credentials</h3>
            <table style="width: 100%; font-size: 14px;">
              <tr><td style="padding: 8px 0; color: #666;">Dashboard URL:</td><td><a href="${PRODUCTION_URL}/admin/login" style="color: #3a87fe;">${PRODUCTION_URL}/admin/login</a></td></tr>
              <tr><td style="padding: 8px 0; color: #666;">Email:</td><td style="font-family: monospace; background: #f1f5f9; padding: 4px 8px; border-radius: 4px;">${emailForm.login_email || '[email]'}</td></tr>
              <tr><td style="padding: 8px 0; color: #666;">Password:</td><td style="font-family: monospace; background: #f1f5f9; padding: 4px 8px; border-radius: 4px;">${emailForm.login_password || '[password]'}</td></tr>
            </table>
          </div>
          
          <div style="background: white; padding: 20px; border-radius: 8px;">
            <h3 style="margin: 0 0 15px 0; color: #5c0701;">What You Can Do</h3>
            <ul style="margin: 0; padding-left: 20px; line-height: 1.8; font-size: 14px;">
              <li><strong>Dashboard</strong> - See all leads, images, and stats at a glance</li>
              <li><strong>CRM & Leads</strong> - Every inquiry captured automatically</li>
              <li><strong>Page Content</strong> - Edit website text instantly</li>
              <li><strong>Media Library</strong> - Upload project photos</li>
              <li><strong>CT Remodel News</strong> - AI-generated blog posts for SEO</li>
              <li><strong>QR Code Generator</strong> - Create codes for marketing materials</li>
              <li><strong>Testimonials</strong> - Manage customer reviews</li>
            </ul>
          </div>
        </div>
        
        <div style="background: #f1f5f9; padding: 15px; text-align: center; font-size: 11px; color: #64748b; border-radius: 0 0 8px 8px;">
          <p style="margin: 0;">Questions? Take a screenshot and text/email it to us.</p>
          <p style="margin: 8px 0 0 0;">— Web Tech Services</p>
        </div>
      </div>
    `;
  };
  
  // Send welcome email
  const handleSendWelcomeEmail = async () => {
    if (!emailForm.recipient_email || !emailForm.login_email || !emailForm.login_password) {
      setEmailStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }
    
    setSendingEmail(true);
    setEmailStatus({ type: '', message: '' });
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/send-welcome-email`,
        {
          recipient_email: emailForm.recipient_email,
          recipient_name: emailForm.recipient_name || 'Client',
          login_email: emailForm.login_email,
          login_password: emailForm.login_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEmailStatus({ type: 'success', message: response.data.message || 'Welcome email sent successfully!' });
      setEmailForm({ recipient_email: '', recipient_name: '', login_email: '', login_password: '' });
      setSelectedUserId('');
      setShowEmailForm(false);
      setShowPreview(false);
    } catch (err) {
      setEmailStatus({ 
        type: 'error', 
        message: err.response?.data?.detail || 'Failed to send email. Please try again.' 
      });
    } finally {
      setSendingEmail(false);
      setTimeout(() => setEmailStatus({ type: '', message: '' }), 5000);
    }
  };

  const features = [
    {
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
      title: 'Dashboard Overview',
      tab: 'dashboard',
      description: 'See your business at a glance — total leads, new inquiries, quoted jobs, won projects, and media counts all in one place.',
      benefits: ['Instant visibility into your pipeline', 'Quick-action links to your live website pages', 'Know exactly where your leads stand']
    },
    {
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      title: 'CRM & Lead Management',
      tab: 'crm',
      description: 'Every person who contacts you through your website is automatically captured here. Track them from first inquiry to completed job.',
      benefits: ['Never lose a lead again', 'Track status: New → Contacted → Quoted → Won', 'Send emails directly to leads from the dashboard', 'Filter and search to find any lead instantly', 'Activity logging shows full communication history']
    },
    {
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      title: 'Page Content Editor',
      tab: 'pages',
      description: 'Edit the text on any page of your website without touching any code. Change headlines, descriptions, and section content instantly.',
      benefits: ['Update your homepage hero text anytime', 'Edit service page descriptions', 'Changes go live immediately', 'SEO-friendly content management']
    },
    {
      icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
      title: 'Media Library',
      tab: 'images',
      description: 'Upload and organize all your project photos. Every image is safely stored in the database — nothing gets lost when the server updates.',
      benefits: ['Upload from your computer or paste a URL (great for phone photos)', 'Add SEO alt text and captions to help Google find your images', 'Organize by category (portfolio, kitchen, bathroom, etc.)', 'Photos appear automatically on the right pages']
    },
    {
      icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
      title: 'Video Manager',
      tab: 'videos',
      description: 'Add YouTube videos of your projects. They display on your Portfolio page as embedded players visitors can watch right on your site.',
      benefits: ['Showcase project timelapse videos', 'Add before/after walkthroughs', 'Videos build trust with potential customers', 'YouTube embeds don\'t slow down your site']
    },
    {
      icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
      title: 'Testimonials',
      tab: 'testimonials',
      description: 'Manage the customer reviews that appear on your homepage and service pages. Show potential customers that real people love your work.',
      benefits: ['Add reviews with star ratings (1-5)', 'Show customer name, city, and service type', 'Hide/show reviews with one click', 'Reviews display beautifully on your homepage', 'Ready for Google Business Profile auto-import']
    },
    {
      icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
      title: 'SMS Messaging',
      tab: 'sms',
      description: 'Send text messages to your leads and customers. Built-in credit system tracks costs. Full TCPA compliance with double opt-in.',
      benefits: ['Two-way text conversations with customers', 'SMS credit balance tracking ($0.015/text)', 'Double opt-in for legal compliance', 'Bulk messaging to opted-in customers', 'Instant lead notifications via text', 'Conversation archive and history']
    },
    {
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      title: 'User Management',
      tab: 'users',
      description: 'Control who has access to this dashboard. Add team members, set permissions, and manage passwords.',
      benefits: ['Add admin accounts for your team', 'Superadmin has full control, Admin manages content', 'Suspend/reactivate accounts instantly', 'Change passwords without calling tech support']
    },
    {
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
      title: 'Settings & SEO',
      tab: 'settings',
      description: 'Configure site-wide settings and submit your pages to search engines for faster indexing.',
      benefits: ['Store important config values (phone, hours, etc.)', 'One-click "Submit to IndexNow" tells Google your site was updated', 'Organized by category for easy management']
    },
    {
      icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
      title: 'CT Remodel News (Auto Blog)',
      tab: 'blog',
      description: 'AI-powered blog that automatically generates SEO-optimized articles about home remodeling. Fresh content helps your website rank higher in Google searches.',
      benefits: ['One-click AI blog post generation (GPT-4o)', 'SEO-optimized content targeting CT homeowners', 'Publish/unpublish posts with one click', 'RSS feed for subscribers', 'Helps build authority and organic traffic']
    },
    {
      icon: 'M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h2m6 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z',
      title: 'QR Code Generator',
      tab: 'qrcode',
      description: 'Create QR codes for your marketing materials. Scan with any smartphone to go directly to your website.',
      benefits: ['Perfect sizes for business cards, yard signs, truck magnets', 'Download as high-quality PNG images', 'Link to any page on your website', 'Great for print marketing and job site signs']
    },
  ];

  const comingSoon = [
    {
      title: 'Google Analytics 4 (GA4) Integration',
      description: 'Track website visitors, page views, and conversions directly in your dashboard. See which pages perform best and where your leads come from.',
      timeline: 'Coming Soon',
    },
    {
      title: 'Google Business Profile Integration',
      description: 'Automatically pull your Google reviews directly into the Testimonials section. See and respond to reviews without leaving your dashboard.',
      timeline: 'Coming in ~60 days',
    },
    {
      title: 'Automated Drip Campaigns',
      description: 'Set up automatic follow-up sequences — your leads get a text after 1 hour, an email after 24 hours, and another text after 3 days, all without you lifting a finger.',
      timeline: 'Phase 2',
    },
    {
      title: 'Live Chat Widget',
      description: 'A chat bubble on your website so visitors can ask questions in real time. Messages appear in your dashboard inbox.',
      timeline: 'Future Enhancement',
    },
  ];

  const emailBody = `Hi Cody,

Welcome to your new CTHomeRemodels.com dashboard! Here's everything you can do:

YOUR DASHBOARD (https://cthomeremodels.com/admin/login)
---
1. DASHBOARD - See all your leads, images, and testimonials at a glance
2. CRM & LEADS - Every website inquiry is captured automatically. Track leads from New to Won.
3. PAGE CONTENT - Edit any text on your website instantly from the dashboard
4. MEDIA LIBRARY - Upload project photos from your computer or phone (via URL)
5. VIDEOS - Add YouTube project videos to your portfolio page
6. TESTIMONIALS - Manage customer reviews with star ratings. Show/hide with one click.
7. SMS MESSAGING - Text your leads directly. Built-in credit tracking at $0.015/text.
8. USER MANAGEMENT - Add team members and control access
9. SETTINGS - Site config and one-click Google/Bing indexing

Every page has a "How do I use this?" button with step-by-step instructions.

---

IMPORTANT NEXT STEP: SET UP GOOGLE BUSINESS PROFILE
---
This is the #1 thing that determines if you show up on Google Maps when people search "remodeling contractor near me." Without it, your website alone won't appear on the map. With it, you'll show up above most competitors.

Here's how (takes about 15 minutes):

1. Go to: business.google.com/add
2. Sign in with your Gmail
3. Click "Add your business to Google"
4. Business name: Use your exact registered name
5. Category: "remodeling contractor" or "home remodeling contractor"
6. Choose "I go to my customers" (hides your home address)
7. Enter your address (private), phone number, website: cthomeremodels.com
8. Add service areas: Meriden, Wallingford, Cheshire, Old Saybrook, Madison, etc.
9. Description: "Licensed CT remodeling contractor serving Meriden and surrounding areas since 2022."
10. Verify your profile (Google will offer phone, text, video, or postcard options)

For video verification (common for contractors):
- Film outside your business address (show house number)
- Show your work truck/tools with company name
- Hold up your CT contractor license
- Say: "This is [Business Name], licensed remodeling contractor in Meriden, CT"
- Upload right away - don't edit it

After approval (1-5 business days):
- Hide your address (show only service areas)
- Add at least 10 project photos
- Ask happy customers for Google reviews

TIPS:
- Use the exact same name/phone/address everywhere
- Your website is already linked - helps Google trust you faster
- Don't add fake reviews - Google catches it

---

COMING SOON:
- Google Business Profile: Auto-import your Google reviews to your website
- Automated drip campaigns: Follow up with leads automatically
- Live SMS notifications for new leads

Your login:
Email: [your email]
Password: [your password]
URL: https://cthomeremodels.com/admin/login

If you get stuck on anything, take a screenshot and text/email it to us.

- Web Tech Services`;

  const copyEmail = () => {
    navigator.clipboard.writeText(emailBody);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  return (
    <div data-testid="welcome-page" className="max-w-5xl mx-auto">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-[#5c0701] to-[#8b1a15] rounded-xl p-8 mb-8 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome to Your CTHomeRemodels Dashboard</h1>
        <p className="text-white/80 text-sm leading-relaxed max-w-3xl">
          This is your command center for managing your website, leads, photos, videos, reviews, and customer communications.
          Everything is designed to be simple — click the <strong>"How do I use this?"</strong> link on any page for step-by-step instructions.
        </p>
      </div>

      {/* QR Code Feature Highlight */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8" data-testid="qr-highlight">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-14 h-14 bg-[#3a87fe] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h2m6 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </div>
          <div className="flex-grow">
            <h3 className="font-bold text-base text-gray-900 mb-1">New: QR Code Generator for Marketing</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Create scannable QR codes for your business cards, yard signs, truck magnets, and flyers. Customers scan with their phone and go straight to your website. 
              <strong className="text-[#3a87fe]"> Click "QR Code Generator" in the left menu to get started!</strong>
            </p>
          </div>
          <div className="flex flex-col gap-2 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Business Cards (150px)
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Yard Signs (500px)
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Truck Magnets (800px)
            </div>
          </div>
        </div>
      </div>

      {/* Feature Cards */}
      <h2 className="text-lg font-bold mb-4 text-gray-800">What You Can Do</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {features.map((f, idx) => (
          <div key={idx} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid={`feature-card-${idx}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#5c0701]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[#5c0701]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d={f.icon} />
                </svg>
              </div>
              <h3 className="font-bold text-sm text-gray-900">{f.title}</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3 leading-relaxed">{f.description}</p>
            <ul className="space-y-1">
              {f.benefits.map((b, i) => (
                <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                  <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* IMPORTANT Next Steps: Google Business Profile Setup - MOVED UP */}
      <h2 className="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">IMPORTANT</span>
        Next Steps: Google Business Profile Setup
      </h2>
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10" data-testid="gbp-setup-guide">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-bold text-base text-gray-900 mb-1">Why This Is Critical for Your Business</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Google Business Profile (GBP) is the <strong>#1 factor</strong> that determines if you show up on Google Maps and in local search results.
              When someone searches "remodeling contractor near me" or "kitchen remodel Meriden CT," Google prioritizes businesses with verified profiles.
              <strong> Without GBP, your website alone won't appear on the map — with it, you'll show up above most competitors.</strong>
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Step 1 */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
            <h4 className="font-bold text-sm mb-2 text-gray-900">Step 1: Check If Your Listing Already Exists</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1">
              <li>Open Google.com on a phone or computer.</li>
              <li>Type your exact business name + "Meriden CT" (e.g., "Visser General Construction Meriden CT").</li>
              <li><strong>If it shows up</strong> — skip to Step 3 and "claim" it.</li>
              <li><strong>If nothing shows</strong> — go to Step 2.</li>
            </ol>
          </div>

          {/* Step 2 */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
            <h4 className="font-bold text-sm mb-2 text-gray-900">Step 2: Create Your Profile (10–15 minutes)</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1.5">
              <li>On a computer, go to: <a href="https://business.google.com/add" target="_blank" rel="noopener noreferrer" className="text-[#3a87fe] underline font-medium">business.google.com/add</a></li>
              <li>Sign in with your Gmail (or create a free one).</li>
              <li>Click <strong>"Add your business to Google"</strong>.</li>
              <li>Type your exact business name (match what's on your license).</li>
              <li>Choose category: Type <strong>"remodeling contractor"</strong> or <strong>"home remodeling contractor"</strong>.</li>
              <li>When asked "Do customers come to your location?" → Choose <strong>"I go to my customers"</strong> (this hides your home address).</li>
              <li>Enter your physical address (private — won't be shown publicly).</li>
              <li>Add your phone number and website: <strong>cthomeremodels.com</strong></li>
              <li>Add hours (or mark as "Open by appointment").</li>
              <li>Add service areas: Type cities like <strong>Meriden, Wallingford, Cheshire, Old Saybrook, Madison</strong> etc. (up to 20).</li>
              <li>Add description: <strong>"Licensed CT remodeling contractor serving Meriden and surrounding areas since 2022."</strong></li>
              <li>Skip photos for now (you'll add them after approval).</li>
            </ol>
          </div>

          {/* Step 3 */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-400">
            <h4 className="font-bold text-sm mb-2 text-gray-900">Step 3: Verify Your Profile (Most Important Step)</h4>
            <p className="text-sm text-gray-600 mb-2">Google needs to confirm your business is real. They'll show verification options:</p>
            <ul className="list-disc ml-5 text-sm text-gray-600 space-y-1.5">
              <li><strong>Easiest option:</strong> Phone call or text — just type the code they send.</li>
              <li><strong>Video verification</strong> (common for contractors) — Google tells you exactly what to film (30–60 seconds):
                <ul className="list-disc ml-5 mt-1 space-y-0.5 text-xs">
                  <li>Film outside your business address (show house number or sign)</li>
                  <li>Show your work truck/tools with the company name if possible</li>
                  <li>Hold up your CT contractor license or business card</li>
                  <li>Say clearly: "This is Visser General Construction, licensed remodeling contractor in Meriden, CT"</li>
                  <li>Film it right away and upload — don't edit it</li>
                </ul>
              </li>
              <li><strong>Postcard:</strong> They mail a code — takes 5–14 days. Enter it when it arrives.</li>
            </ul>
          </div>

          {/* Step 4 */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-yellow-400">
            <h4 className="font-bold text-sm mb-2 text-gray-900">Step 4: Wait for Approval (Up to 5 Business Days)</h4>
            <ul className="list-disc ml-5 text-sm text-gray-600 space-y-1">
              <li>Google reviews your submission (usually 1–5 business days).</li>
              <li>You'll get an email when approved (blue checkmark appears).</li>
              <li>If rejected (rare if info is accurate), Google explains why. Fix it and re-verify.</li>
            </ul>
          </div>

          {/* Step 5 */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-400">
            <h4 className="font-bold text-sm mb-2 text-gray-900">Step 5: After Approval — Do This Right Away</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1.5">
              <li>Log back into <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-[#3a87fe] underline">business.google.com</a></li>
              <li>Edit profile → Location → Turn on <strong>"Hide address from customers"</strong> (only service areas will show).</li>
              <li>Add <strong>at least 10 photos</strong>: before/after project photos, your truck, team, license.</li>
              <li>Add services: "Kitchen remodeling, bathroom remodeling, deck building," etc.</li>
              <li><strong>Ask happy customers for reviews</strong> — this is the #1 way to rank higher.</li>
            </ol>
          </div>

          {/* Tips */}
          <div className="bg-[#5c0701]/5 rounded-lg p-4 border border-[#5c0701]/20">
            <h4 className="font-bold text-sm mb-2 text-[#5c0701]">Tips for Fastest Approval</h4>
            <ul className="list-disc ml-5 text-sm text-gray-600 space-y-1">
              <li>Use the <strong>exact same business name, phone, and address</strong> everywhere (website, Facebook, CT license records).</li>
              <li>Your website (cthomeremodels.com) is already linked — this helps Google trust your profile faster.</li>
              <li>Don't add fake reviews or wrong info — Google catches it and can suspend the profile.</li>
              <li>Once live, your business will show on <strong>Google Maps and Search</strong> when people search for contractors near you.</li>
            </ul>
            <p className="text-xs text-gray-500 mt-3">This is the official Google process — no paid services needed. If you get stuck on any screen, take a screenshot and text/email it to us.</p>
          </div>
          
          {/* Admin Access Request */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mt-4">
            <h4 className="font-bold text-sm mb-2 text-blue-800">🔑 We'll Need Your GBP Login Credentials</h4>
            <p className="text-sm text-gray-600 mb-2">
              Once your Google Business Profile is set up and verified, please share your GBP login credentials with us so we can:
            </p>
            <ul className="list-disc ml-5 text-sm text-gray-600 space-y-1 mb-3">
              <li>Add <strong>web-techservices.com</strong> as an admin user on your GBP account</li>
              <li>Make future SEO updates and optimizations on your behalf</li>
              <li>Respond to reviews and update business info when needed</li>
              <li>Set up the auto-import of Google reviews to your website</li>
            </ul>
            <p className="text-xs text-gray-500">
              This is a one-time setup. Once we're added as an admin, you can change your password and we'll still have access to manage your profile.
            </p>
          </div>
        </div>
      </div>

      {/* Coming Soon - MOVED AFTER IMPORTANT NEXT STEPS */}
      <h2 className="text-lg font-bold mb-4 text-gray-800">Coming Soon</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {comingSoon.map((item, idx) => (
          <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid={`coming-soon-${idx}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded">{item.timeline}</span>
            </div>
            <h3 className="font-bold text-sm text-gray-900 mb-1">{item.title}</h3>
            <p className="text-xs text-gray-600 leading-relaxed">{item.description}</p>
          </div>
        ))}
      </div>

      {/* Client Welcome Email — Superadmin Only */}
      {userRole === 'superadmin' && (
      <>
      <h2 className="text-lg font-bold mb-4 text-gray-800">Client Welcome Email Template</h2>
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4" data-testid="welcome-email-template">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs text-gray-500">Copy this email and send it to the client with their login credentials:</p>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowEmailForm(!showEmailForm)} 
              className="bg-[#5c0701] text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-[#7a0901] transition" 
              data-testid="send-email-btn"
            >
              {showEmailForm ? 'Cancel' : 'Send Email Now'}
            </button>
            <button onClick={copyEmail} className="bg-[#3a87fe] text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-[#2968d9] transition" data-testid="copy-email-btn">
              {emailCopied ? 'Copied!' : 'Copy Email Text'}
            </button>
          </div>
        </div>
        
        {/* Email Status Message */}
        {emailStatus.message && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            emailStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`} data-testid="email-status">
            {emailStatus.message}
          </div>
        )}
        
        {/* Send Welcome Email Form */}
        {showEmailForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4" data-testid="send-email-form">
            <h3 className="font-bold text-sm mb-3 text-gray-900">Send Welcome Email to Client</h3>
            <p className="text-xs text-gray-500 mb-4">Select a user or enter details manually. The email includes their login credentials and the $5,000+ package value.</p>
            
            {/* User Dropdown */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Quick Select User (Optional)</label>
              <select
                value={selectedUserId}
                onChange={e => handleUserSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent bg-white"
                data-testid="user-select"
              >
                <option value="">-- Select existing user --</option>
                {users.filter(u => u.role !== 'superadmin').map(u => (
                  <option key={u.email} value={u.email}>{u.email} ({u.role})</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client's Email *</label>
                <input 
                  type="email"
                  value={emailForm.recipient_email}
                  onChange={e => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                  placeholder="client@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                  data-testid="email-recipient"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client's Name</label>
                <input 
                  type="text"
                  value={emailForm.recipient_name}
                  onChange={e => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                  placeholder="e.g., Cody"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                  data-testid="email-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Dashboard Login Email *</label>
                <input 
                  type="text"
                  value={emailForm.login_email}
                  onChange={e => setEmailForm({ ...emailForm, login_email: e.target.value })}
                  placeholder="Same as client email usually"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                  data-testid="email-login"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Dashboard Password *</label>
                <input 
                  type="text"
                  value={emailForm.login_password}
                  onChange={e => setEmailForm({ ...emailForm, login_password: e.target.value })}
                  placeholder="Enter the password you set"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
                  data-testid="email-password"
                />
                <p className="text-xs text-gray-400 mt-1">Must enter manually for security</p>
              </div>
            </div>
            
            {/* URL Check Warning */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-green-700 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Production URL verified:</strong> Email will use <code className="bg-green-100 px-1 rounded">cthomeremodels.com</code> (not preview URL)</span>
              </p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => setShowPreview(true)}
                disabled={!emailForm.recipient_email || !emailForm.login_email || !emailForm.login_password}
                className="bg-[#3a87fe] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="preview-email-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview Email
              </button>
            </div>
          </div>
        )}
        
        {/* Email Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-lg">Email Preview</h3>
                  <p className="text-xs text-gray-500">Review before sending to {emailForm.recipient_email}</p>
                </div>
                <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]" dangerouslySetInnerHTML={{ __html: generatePreviewHTML() }} />
              
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
                <p className="text-xs text-gray-500">
                  <span className="text-green-600 font-medium">✓ Using production URL:</span> cthomeremodels.com
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowPreview(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
                    Edit
                  </button>
                  <button 
                    onClick={handleSendWelcomeEmail}
                    disabled={sendingEmail}
                    className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {sendingEmail ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Email Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed max-h-[400px] overflow-y-auto border border-gray-100">
          {emailBody}
        </pre>
      </div>
      </>
      )}
    </div>
  );
};

export default WelcomePage;
